import { Box, Grid } from '@mui/material'
import React from 'react'
import { useDashboardTrackingJobsQuery, useJobsQuery } from '@typings/graphql'
import { useNavigate, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { palette } from '@theme/core/palette/palette'
import { mapYearData, sumYearData } from '@utils/dashboard'
import { CONVERSION_TIME_SPAN } from '@shared/constants/dashboard'
import { DashboardItemList, DashboardTopItemsType } from '@shared/components/cards/DashboardItemList'
import { DashboardConversionRate } from '@shared/components/cards/DashboardConversionRate'
import { DashboardClicks } from '@shared/components/cards/DashboardClicks'
import { DashboardYearOverview } from '@shared/components/cards/DashboardYearOverview'
import { PerformanceChip } from '@shared/components/ui/PerformanceChip'

export const DashboardJobView: React.FC = () => {
  const { t } = useTranslation()

  const { id } = useParams<{id: string}>()

  const { data } = useJobsQuery({
    variables: {
      companyId: id
    },
    fetchPolicy: 'cache-and-network'
  })

  const { data: dashboardData } = useDashboardTrackingJobsQuery({
    variables: {
      companyId: id
    }
  })

  const navigate = useNavigate()

  const mappedDashboardData = React.useMemo(() => {
    if (!dashboardData) {
      return {}
    }

    const {
      job_click: buttonClick
    } = dashboardData?.dashboardTrackingJobs || {}

    const buttonClicks = buttonClick ? sumYearData(buttonClick) : []

    return {
      partnerClicks: (dashboardData?.dashboardTrackingJobs?.partner_click || []) as number[][],
      buttonClicks
    }
  }, [dashboardData])

  const mappedJobs = React.useMemo<DashboardTopItemsType[]>(() => {
    return data?.jobs.sort((a, b) => b.bimonthlyPerformance - a.bimonthlyPerformance).map((job) => ({
      id: job.id,
      name: job.texts?.name,
      extension: <PerformanceChip value={job.bimonthlyPerformance} />
    })
    ) || []
  }, [data])

  const mappedYearData = React.useMemo(() => {
    if (!mappedDashboardData) {
      return []
    }

    return [
      {
        name: 'jobApplicationClicks',
        color: palette.primary[900] || '',
        values: mapYearData(mappedDashboardData.buttonClicks || [])
      },
      {
        name: 'jobClicks',
        color: palette.secondary[300] || '',
        values: mapYearData(mappedDashboardData.partnerClicks || [])
      }
    ]
  }, [mappedDashboardData])

  const conversionData = React.useMemo(() => {
    const { buttonClicks, partnerClicks } = mappedDashboardData

    const flattenedButtonClicks = buttonClicks?.flat() || []
    const flattenedPartnerClicks = partnerClicks?.flat() || []

    return {
      secondary: flattenedButtonClicks.slice(flattenedButtonClicks.length -
        CONVERSION_TIME_SPAN, flattenedButtonClicks.length).reduce((a, b) => a + b, 0),
      primary: flattenedPartnerClicks.slice(flattenedPartnerClicks.length -
        CONVERSION_TIME_SPAN, flattenedPartnerClicks.length).reduce((a, b) => a + b, 0)
    }
  }, [mappedDashboardData])

  return (
    <Grid container spacing={3}>
      <Grid height="auto" item xs={9} xl={10}>
        <DashboardYearOverview
          data={mappedYearData}
          colorSecondary
          showYAxis
        />
      </Grid>
      <Grid item xs={3} xl={2}>
        {
          <DashboardConversionRate
            colorSecondary
            titlePrimary={t('dashboard.jobApplicationClicks')}
            titleSecondary={t('dashboard.jobClicks')}
            primary={conversionData.primary}
            secondary={conversionData.secondary}
          />
        }
      </Grid>
      <Grid item xs={6}>
        <Box mb={1}>
          {
            <DashboardClicks
              keyVal="jobs"
              title={t('dashboard.applicationClicks')}
              values={mappedDashboardData.buttonClicks || []}
            />
          }
        </Box>
      </Grid>
      <Grid item
        xs={6}
        sx={{
          height: '100%'
        }}
      >
        <DashboardItemList
          title={t('dashboard.topJobs')}
          data={mappedJobs}
          onItemClicked={(jobId) => navigate(`/jobwall/${jobId}`)}
          onShowAll={() => navigate('/jobwall')}
        />
      </Grid>
    </Grid>
  )
}
